export type ReviewEvent<T> = { type: 'run' | 'done'; run: T } | { type: 'step'; label: string } | { type: 'ping' } | { type: 'error'; error: string };

/** Keep long account reviews alive, with their saved run ID available for recovery. */
export async function runReview<T>(path: string, onEvent: (event: ReviewEvent<T>) => void, signal?: AbortSignal): Promise<{ run: T }> {
  const response = await fetch(path, { method: 'POST', credentials: 'include', signal,
    headers: { Accept: 'application/x-ndjson', 'Content-Type': 'application/json', 'X-NL-AOS-Request': '1' }, body: '{}' });
  if (!response.ok || !response.body) {
    const data = await response.json().catch(() => null) as { error?: string } | null;
    throw new Error(data?.error || `The review could not start (${response.status}).`);
  }
  const reader = response.body.getReader(); const decoder = new TextDecoder(); let buffer = '', result: { run: T } | undefined;
  try {
    for (;;) {
      const { value, done } = await reader.read();
      signal?.throwIfAborted();
      buffer += decoder.decode(value, { stream: !done });
      const lines = (done ? buffer+'\n' : buffer).split('\n'); buffer = lines.pop() || '';
      for (const line of lines.filter(l => l.trim())) {
        const event = JSON.parse(line) as ReviewEvent<T>;
        if (event.type === 'error') throw new Error(event.error);
        onEvent(event);
        if (event.type === 'done') result = { run: event.run };
      }
      if (done) break;
    }
    if (!result) throw new Error('The connection ended. The saved review may still be running.');
    return result;
  } finally { await reader.cancel().catch(() => undefined); reader.releaseLock(); }
}
